import { apiFetch } from '../api/client';
import type { TranscribeAudioResponse } from '../hooks/useLessonApi';

/** Ids that tie the upload's telemetry to the run and step (TR-037). */
export interface TranscribeRunContext {
  runId?: string | null;
  stepIndex?: number | null;
}

export async function transcribeAudio(
  audioBlob: Blob,
  languageCode: string = 'el-GR',
  signal?: AbortSignal,
  phraseHints?: string[] | null,
  runContext?: TranscribeRunContext
): Promise<TranscribeAudioResponse> {
  // Check if aborted before doing any async work
  if (signal?.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    throw abortError;
  }

  const formData = new FormData();
  formData.append('audio', audioBlob);
  formData.append('lang', languageCode);
  if (phraseHints && phraseHints.length > 0) {
    phraseHints.forEach((hint) => formData.append('phrase_hints', hint));
  }

  // Ids only, appended when known; the backend uses them to correlate logs.
  if (runContext?.runId) formData.append('run_id', runContext.runId);
  if (runContext?.stepIndex != null) {
    formData.append('step_index', String(runContext.stepIndex));
  }

  const response = await apiFetch('/speech/utterance', {
    method: 'POST',
    headers: { 'Idem-Key': crypto.randomUUID() },
    body: formData,
    signal,
  });

  if (!response.ok) {
    let errorMessage = `Failed to transcribe audio: ${response.statusText}`;
    let errorDetail: string | null = null;

    try {
      const errorData = await response.json();
      if (errorData.detail) {
        errorDetail = errorData.detail;
        errorMessage = errorData.detail;
      } else if (typeof errorData === 'string') {
        errorDetail = errorData;
        errorMessage = errorData;
      } else if (errorData.message) {
        errorDetail = errorData.message;
        errorMessage = errorData.message;
      }
    } catch {
      // If response is not JSON, use status text
    }

    console.error('Transcribe audio error:', {
      status: response.status,
      statusText: response.statusText,
      detail: errorDetail,
    });

    if (response.status === 400) {
      throw new Error('Invalid request. Please check audio format.');
    }
    if (response.status === 413) {
      throw new Error('Audio file too large. Maximum size is 4MB.');
    }
    if (response.status === 422) {
      throw new Error(`Validation error: ${errorMessage}`);
    }
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      throw new Error(
        `Too many requests. Please wait ${retryAfter || 60} seconds before trying again.`
      );
    }
    if (response.status === 503) {
      throw new Error(
        'Speech recognition service temporarily unavailable. Please try again later.'
      );
    }
    if (response.status === 500) {
      throw new Error(
        errorDetail ||
          'Server error occurred. Please check the browser console for details.'
      );
    }
    throw new Error(errorMessage);
  }

  return response.json();
}
